// context/ThemeContext.jsx
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  getChipColors, 
  getSemanticColors, 
  getSurfaceColors,
  getCategoryCardColors,
} from '../utils/colors';

const ThemeContext = createContext();

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: {
            main: '#00B4D8',
            dark: '#0077B6',
            light: '#48CAE4',
          },
          secondary: {
            main: '#7209B7',
          },
          background: {
            default: '#F5F5F5',
            paper: '#FFFFFF',
          },
          text: {
            primary: '#1A1A1A',
            secondary: '#6B6B6B',
          },
          success: {
            main: '#16a34a',
            light: '#dcfce7',
          },
          error: {
            main: '#FF6B6B',
            light: '#fee2e2',
          },
          warning: {
            main: '#FFD23F',
            light: '#fef3c7',
          },
          info: {
            main: '#00B4D8',
          },
          divider: 'rgba(0, 0, 0, 0.12)',
        }
      : {
          primary: {
            main: '#48CAE4',
            dark: '#00B4D8',
            light: '#90E0EF',
          },
          secondary: {
            main: '#a78bfa',
            light: '#c4b5fd',
            dark: '#7209B7',
          },
          background: {
            default: '#121212',
            paper: '#1e1e1e',
          },
          text: {
            primary: '#e8eaed',
            secondary: '#9aa0a6',
          },
          success: {
            main: '#22c55e',
            light: '#166534',
          },
          error: {
            main: '#ef4444',
            light: '#7f1d1d',
          },
          warning: {
            main: '#f59e0b',
            light: '#78350f',
          },
          info: {
            main: '#48CAE4',
          },
          divider: 'rgba(255, 255, 255, 0.12)',
        }),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 500, textTransform: 'none' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: mode === 'dark' ? '#6b6b6b #2b2b2b' : '#c1c1c1 #f1f1f1',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: mode === 'dark' ? '#6b6b6b' : '#c1c1c1',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: mode === 'dark' ? '#2b2b2b' : '#f1f1f1',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: mode === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.4)'
            : '0 2px 8px rgba(0, 0, 0, 0.1)',
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 20px',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

const getInitialMode = () => {
  const saved = localStorage.getItem('themeMode');
  if (saved) return saved;
  
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

export const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);

  const isDark = mode === 'dark';

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      if (!localStorage.getItem('themeMode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  const setTheme = (newMode) => {
    localStorage.setItem('themeMode', newMode);
    setMode(newMode);
  };

  const resetToSystemTheme = () => {
    localStorage.removeItem('themeMode');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setMode(systemPrefersDark ? 'dark' : 'light');
  };

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  // Memoize colors so they don't recalculate on every render
  const colors = useMemo(() => ({
    chip: getChipColors(isDark),
    semantic: getSemanticColors(isDark),
    surface: getSurfaceColors(isDark),
    getCategoryCard: (color) => getCategoryCardColors(color, isDark),
  }), [isDark]);

  const value = {
    mode,
    isDark,
    toggleTheme,
    setTheme,
    resetToSystemTheme,
    colors,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};